// Pure helpers for `recipeBulkImportPreview.ts`. Split out so the main
// file stays under the file-size hygiene threshold and so each piece is
// individually testable.

import type {
  MfgMaterial,
  MfgMaterialUse,
  MfgRecipeKind,
  MfgRecipeLineRole,
  MfgRecipeSummary,
} from "./types";
import type {
  ImportRecipeLine,
  ImportRecipeLineKind,
} from "./recipeBulkImportPreviewTypes";

const RECIPE_KINDS: ReadonlySet<MfgRecipeKind> = new Set([
  "assembly",
  "variant",
  "option",
  "package",
  "product",
]);
const RECIPE_LINE_ROLES: ReadonlySet<MfgRecipeLineRole> = new Set([
  "structural",
  "standard_option",
  "upgrade_option",
]);
const LINE_KINDS: ReadonlySet<ImportRecipeLineKind> = new Set([
  "material",
  "component",
  "use",
]);

export function parseNumber(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string" && v.trim() !== "") return parseFloat(v);
  return NaN;
}

export function parseBoolean(v: unknown): boolean | undefined {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true" || s === "yes" || s === "1") return true;
    if (s === "false" || s === "no" || s === "0") return false;
  }
  return undefined;
}

export function parseKind(v: unknown): MfgRecipeKind {
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (RECIPE_KINDS.has(s as MfgRecipeKind)) return s as MfgRecipeKind;
  }
  return "assembly";
}

export function parseRole(v: unknown): MfgRecipeLineRole {
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (RECIPE_LINE_ROLES.has(s as MfgRecipeLineRole)) return s as MfgRecipeLineRole;
  }
  return "structural";
}

export function parseLineKind(
  raw: unknown,
  materialName: string,
  componentCode: string,
  useCode: string,
): ImportRecipeLineKind {
  if (typeof raw === "string") {
    const s = raw.trim().toLowerCase();
    if (LINE_KINDS.has(s as ImportRecipeLineKind)) return s as ImportRecipeLineKind;
  }
  if (componentCode && !materialName && !useCode) return "component";
  if (useCode && !materialName && !componentCode) return "use";
  return "material";
}

// CSV imports use `;` as inner separator (commas are CSV cell delimiters);
// JSON imports use a real array.
export function parseTags(v: unknown): string[] {
  if (Array.isArray(v)) {
    const out: string[] = [];
    for (const t of v) if (typeof t === "string" && t.trim()) out.push(t.trim());
    return out;
  }
  if (typeof v === "string") {
    return v
      .split(/[;,]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  }
  return [];
}

/** Mirrors server-side `normalizeMaterialName`. Keep in sync. */
export function normalizeMaterialName(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, " ");
}

export function normalizeRecipeCode(raw: string): string {
  return raw.trim();
}

export function normalizeUseCode(raw: string): string {
  return raw.trim().toLowerCase();
}

export function buildMaterialIndex(
  materials: ReadonlyArray<MfgMaterial>,
): Map<string, MfgMaterial> {
  const idx = new Map<string, MfgMaterial>();
  for (const m of materials) {
    const k = normalizeMaterialName(m.name);
    if (!idx.has(k)) idx.set(k, m);
  }
  return idx;
}

export function buildRecipeCodeSet(
  recipes: ReadonlyArray<MfgRecipeSummary>,
): Set<string> {
  const s = new Set<string>();
  for (const r of recipes) if (r.code) s.add(normalizeRecipeCode(r.code));
  return s;
}

export function buildRecipeCodeIndex(
  recipes: ReadonlyArray<MfgRecipeSummary>,
): Map<string, MfgRecipeSummary> {
  const idx = new Map<string, MfgRecipeSummary>();
  for (const r of recipes) {
    const k = normalizeRecipeCode(r.code);
    if (k && !idx.has(k)) idx.set(k, r);
  }
  return idx;
}

export function buildMaterialUseCodeIndex(
  uses: ReadonlyArray<MfgMaterialUse>,
): Map<string, MfgMaterialUse> {
  const idx = new Map<string, MfgMaterialUse>();
  for (const u of uses) {
    if (!u.is_active) continue;
    const k = normalizeUseCode(u.use_code);
    if (k && !idx.has(k)) idx.set(k, u);
  }
  return idx;
}

export type RawRow = Record<string, unknown>;

export type BuildLineIndexes = {
  materialIndex: Map<string, MfgMaterial>;
  recipeCodeIndex: Map<string, MfgRecipeSummary>;
  useCodeIndex: Map<string, MfgMaterialUse>;
};

// Normalize a single line. Returns `null` if the line is blank or missing
// required fields (the caller decides whether to push a skipped record).
export function buildLine(
  raw: RawRow,
  idx: number,
  indexes: BuildLineIndexes,
  source: "csv" | "json",
): { line: ImportRecipeLine } | { skipped: { missing: string[] } } | null {
  const nameRaw = source === "csv" ? raw.line_material_name : raw.material_name;
  const componentRaw =
    source === "csv" ? raw.line_component_recipe_code : raw.component_recipe_code;
  const useRaw = source === "csv" ? raw.line_material_use_code : raw.material_use_code;
  const kindRaw = source === "csv" ? raw.line_kind : raw.line_kind ?? raw.kind;
  const qtyRaw = source === "csv" ? raw.line_quantity : raw.quantity;
  const roleRaw = source === "csv" ? raw.line_role : raw.role;
  const slotRaw = source === "csv" ? raw.line_slot_label : raw.slot_label;
  const upgradeRaw =
    source === "csv" ? raw.line_upgrade_price_delta : raw.upgrade_price_delta;
  const swappableRaw = source === "csv" ? raw.line_swappable : raw.swappable;

  const material_name = typeof nameRaw === "string" ? nameRaw.trim() : "";
  const component_recipe_code =
    typeof componentRaw === "string" ? normalizeRecipeCode(componentRaw) : "";
  const material_use_code =
    typeof useRaw === "string" ? normalizeUseCode(useRaw) : "";
  const qtyN = parseNumber(qtyRaw);
  const quantity = Number.isFinite(qtyN) && qtyN > 0 ? qtyN : NaN;
  const line_kind = parseLineKind(
    kindRaw,
    material_name,
    component_recipe_code,
    material_use_code,
  );

  // Completely blank line cells → "no line on this row".
  const blank =
    !material_name &&
    !component_recipe_code &&
    !material_use_code &&
    !Number.isFinite(qtyN);
  if (blank) return null;

  const missing: string[] = [];
  if (!Number.isFinite(quantity)) missing.push("line_quantity");
  if (line_kind === "material" && !material_name) missing.push("line_material_name");
  if (line_kind === "component" && !component_recipe_code) {
    missing.push("line_component_recipe_code");
  }
  if (line_kind === "use" && !material_use_code) missing.push("line_material_use_code");
  if (missing.length > 0) return { skipped: { missing } };

  const role = parseRole(roleRaw);
  const slot_label = typeof slotRaw === "string" ? slotRaw.trim() : "";
  const upgradeN = parseNumber(upgradeRaw);
  const upgrade_price_delta =
    Number.isFinite(upgradeN) && upgradeN >= 0 ? upgradeN : null;
  const swappableParsed = parseBoolean(swappableRaw);
  const swappable = swappableParsed === undefined ? role !== "structural" : swappableParsed;

  let matched_material_id: string | null = null;
  let matched_component_recipe_id: string | null = null;
  let matched_material_use_id: string | null = null;

  if (line_kind === "material") {
    const matched = indexes.materialIndex.get(normalizeMaterialName(material_name));
    matched_material_id = matched ? matched.id : null;
  } else if (line_kind === "component") {
    const matched = indexes.recipeCodeIndex.get(component_recipe_code);
    matched_component_recipe_id = matched ? matched.id : null;
  } else {
    const matched = indexes.useCodeIndex.get(material_use_code);
    matched_material_use_id = matched ? matched.id : null;
  }

  return {
    line: {
      idx,
      line_kind,
      material_name: line_kind === "material" ? material_name : "",
      component_recipe_code: line_kind === "component" ? component_recipe_code : "",
      material_use_code: line_kind === "use" ? material_use_code : "",
      quantity,
      role,
      slot_label,
      upgrade_price_delta,
      swappable,
      matched_material_id,
      matched_component_recipe_id,
      matched_material_use_id,
    },
  };
}

export function lineIsMatched(line: ImportRecipeLine): boolean {
  if (line.line_kind === "material") return Boolean(line.matched_material_id);
  if (line.line_kind === "component") return Boolean(line.matched_component_recipe_id);
  return Boolean(line.matched_material_use_id);
}

export function lineIsPendingMaterial(line: ImportRecipeLine): boolean {
  return line.line_kind === "material" && !line.matched_material_id;
}

export function lineIsUnresolvedRef(line: ImportRecipeLine): boolean {
  return (
    (line.line_kind === "component" && !line.matched_component_recipe_id) ||
    (line.line_kind === "use" && !line.matched_material_use_id)
  );
}

export function trackIgnoredKeys(
  obj: RawRow,
  recognized: Set<string>,
  sink: Set<string>,
) {
  for (const k of Object.keys(obj)) if (!recognized.has(k)) sink.add(k);
}
